import clsx from "clsx";
import { ReactNode, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";

import { InfoCircle, Warning } from "@/assets/icons/ds";
import { DireProgressBar } from "@/components/Layout/ProgressBar/DireProgressBar";
import {
  RoamingInfoRoamingStatus,
  RoamingInfoUsageZone,
} from "@/services/model";
import {
  formatFlatMobileRoamingInGb,
  formatRoamingTotalInGb,
  formatRoamingUsageInGb,
  isZone1,
} from "@/utils/deviceUtils";
import { Status } from "@/utils/enums";

import { RoamingCallExplanation } from "./RoamingCallExplanation";
import { TarifBarHeadline } from "./TarifBarHeadline";

type RoamingUsageProps = {
  days: number;
  /** Days a package can be activated in total, for "X of Y days to activate". */
  activationDays?: number;
  description: string;
  descriptionExtension?: string;
  partialHint?: ReactNode;
  initial: number;
  isFirstOfUsageZone: boolean;
  remaining: number;
  consumed?: number;
  title: string;
  isIncludedInService?: boolean;
  roamingStatus?: RoamingInfoRoamingStatus;
  isYearly?: boolean;
  showFlatMobileExplanation?: boolean;
  showRoamingCallExplanation?: boolean;
  isNotAllowedToUse?: boolean;
  shouldTreatAsActive: boolean;
  hasIncludedRoamingCalls: boolean;
  isRoamingCallType?: boolean;
  zone?: RoamingInfoUsageZone | string;
  isDataUsage?: boolean;
  icon?: ReactNode;
};

const getPercentage = (
  initial: number | undefined,
  remaining: number | undefined,
) => {
  if (initial !== undefined && remaining !== undefined && initial > 0) {
    // Remaining capacity: full when unused, depletes as GB/min are consumed.
    return Math.min(100, Math.max(0, (remaining / initial) * 100));
  }

  return 0;
};

/**
 * A text row rendered below a tarif-bar usage row, e.g. a renewal or
 * exhausted-usage hint (Figma tarif-bar, body-compact/default/xs).
 */
const NoteRow = ({
  icon,
  children,
}: {
  icon?: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex w-full items-start gap-3">
    {icon}
    <div className="flex-1 text-body-xs leading-none text-foreground-primary">
      {children}
    </div>
  </div>
);

// Figma tarif-bar: renewal and pro-rata hints carry a 24px information-circle.
const noteInfoIcon = (
  <InfoCircle className="size-6 shrink-0 text-foreground-primary" />
);

/**
 * Calculates and displays the data usage of a roaming option.
 * TODO: Should be split into smaller components later on
 */
export const RoamingUsage = ({
  days,
  activationDays,
  description,
  descriptionExtension,
  partialHint,
  initial,
  isFirstOfUsageZone,
  remaining,
  consumed,
  title,
  isIncludedInService,
  roamingStatus,
  isYearly,
  showFlatMobileExplanation,
  showRoamingCallExplanation,
  isNotAllowedToUse,
  shouldTreatAsActive,
  hasIncludedRoamingCalls,
  isRoamingCallType,
  zone,
  isDataUsage,
  icon,
}: RoamingUsageProps) => {
  const [usage, setUsage] = useState<number | undefined>(0);
  const { t } = useTranslation("portal");

  // For data usage: Zone 1 displays in GB, Zone 2/3 display in MB
  const useGB = isDataUsage && isZone1(zone);
  const formatRemainingInGb = isIncludedInService
    ? formatFlatMobileRoamingInGb
    : formatRoamingUsageInGb;
  const formatInitialInGb = isIncludedInService
    ? formatFlatMobileRoamingInGb
    : formatRoamingTotalInGb;

  const isActive = roamingStatus === Status.active || shouldTreatAsActive;

  useEffect(() => {
    setUsage(isActive ? getPercentage(initial, remaining) : undefined);
  }, [roamingStatus, isActive, initial, remaining]);

  const isUsedUpMobileAboRoaming = isIncludedInService && remaining === 0;
  const isOverspentMobileAboRoaming = isIncludedInService && remaining < 0;
  const isFlatMobileRoamingCallCase =
    isRoamingCallType && hasIncludedRoamingCalls;
  const isWaitingForActivation =
    roamingStatus === Status.ready && !isFlatMobileRoamingCallCase;
  const readyCase = isFirstOfUsageZone
    ? "firstPackageOfUsageZone"
    : "notFirstPackageOfUsageZone";
  // Figures stand out in primary, the words around them stay secondary.
  const figure = <span key="0" className="text-foreground-primary" />;
  const hasNotes =
    isOverspentMobileAboRoaming ||
    (!isYearly && (!!descriptionExtension || !!partialHint));

  const PackageExplanation = () => {
    if (showFlatMobileExplanation && !isUsedUpMobileAboRoaming) {
      return <MobileAboProRataExplanation title={title} />;
    } else if (showRoamingCallExplanation && !isNotAllowedToUse) {
      return <RoamingCallExplanation title={title} />;
    } else if (showRoamingCallExplanation && isNotAllowedToUse) {
      return (
        <TarifBarHeadline
          title={title}
          tooltipContent={
            <p>
              {t(
                "cockpit.managementTile.roaming.showOption.active.notAllowedTooltip",
              )}
            </p>
          }
        />
      );
    } else {
      return <TarifBarHeadline title={title} />;
    }
  };

  return (
    <div
      className={clsx(
        "flex w-full flex-col gap-3 rounded-md bg-surface-quaternary p-4",
        isUsedUpMobileAboRoaming && "opacity-50",
      )}
    >
      <div className="flex w-full items-start gap-3">
        {icon}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="flex w-full flex-col gap-1">
            <PackageExplanation />
            {isWaitingForActivation && (
              <p className="text-body-sm leading-none text-foreground-secondary">
                {t(
                  `cockpit.managementTile.roaming.showOption.ready.${readyCase}.subtitle`,
                )}
              </p>
            )}
          </div>

          <div className="flex w-full flex-col gap-2">
            <DireProgressBar
              value={
                isNotAllowedToUse
                  ? undefined
                  : isWaitingForActivation
                    ? 100
                    : roamingStatus === RoamingInfoRoamingStatus.ready &&
                        consumed === 0
                      ? undefined
                      : usage
              }
            />

            <span className="text-body-sm font-semibold leading-none text-foreground-secondary">
              {roamingStatus === "ordered" && (
                <span>
                  {t("cockpit.managementTile.roaming.showOption.ordered.usage")}
                </span>
              )}

              {isWaitingForActivation && (
                <Trans
                  components={[figure]}
                  t={t}
                  values={{ days, initial: activationDays }}
                >
                  {activationDays != null
                    ? "cockpit.managementTile.roaming.showOption.ready.daysLeft"
                    : "cockpit.managementTile.roaming.showOption.ready.daysLeftWithoutTotal"}
                </Trans>
              )}
              {(roamingStatus === Status.active ||
                isFlatMobileRoamingCallCase) && (
                <span>
                  {isNotAllowedToUse ? (
                    <span>
                      {t(
                        "cockpit.managementTile.roaming.showOption.active.notAllowed",
                      )}
                    </span>
                  ) : (
                    <Trans
                      components={[figure]}
                      values={{
                        // These messages all read "X of Y available",
                        // so the displayed value is always what's left, not
                        // what's been used.
                        value: useGB
                          ? formatRemainingInGb(remaining)
                          : remaining,
                        initial: useGB ? formatInitialInGb(initial) : initial,
                      }}
                      t={t}
                    >
                      {useGB ? `${description}GB` : description}
                    </Trans>
                  )}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {hasNotes && (
        <div className="flex w-full flex-col gap-2.5">
          {isOverspentMobileAboRoaming && (
            <NoteRow icon={<Warning className="size-6 shrink-0" />}>
              <Trans
                components={[
                  <Link
                    key="0"
                    className="underline"
                    to={t(
                      "cockpit.managementTile.roaming.showOption.active.overspentCallsLink",
                    )}
                    target="_blank"
                  />,
                ]}
                t={t}
              >
                {t(
                  "cockpit.managementTile.roaming.showOption.active.overspentCalls",
                )}
              </Trans>
            </NoteRow>
          )}

          {!isYearly && descriptionExtension && (
            <NoteRow icon={noteInfoIcon}>{descriptionExtension}</NoteRow>
          )}

          {!isYearly && partialHint && (
            <NoteRow icon={noteInfoIcon}>{partialHint}</NoteRow>
          )}
        </div>
      )}
    </div>
  );
};

const MobileAboProRataExplanation = ({ title }: { title: string }) => {
  const { t } = useTranslation("portal");

  return (
    <TarifBarHeadline
      title={title}
      tooltipContent={
        <>
          <span className="mb-1 block text-body-sm font-semibold leading-md">
            {t(
              "cockpit.managementTile.roaming.showOption.flatMobileTooltip.title",
            )}
          </span>
          <ul className="list-disc pl-4">
            <li>
              <b>
                {t(
                  "cockpit.managementTile.roaming.showOption.flatMobileTooltip.monthlyRenewal.title",
                )}
              </b>
              <br />
              {t(
                "cockpit.managementTile.roaming.showOption.flatMobileTooltip.monthlyRenewal.description",
              )}
            </li>
            <li>
              <b>
                {t(
                  "cockpit.managementTile.roaming.showOption.flatMobileTooltip.proRataVolume.title",
                )}
              </b>
              <br />
              {t(
                "cockpit.managementTile.roaming.showOption.flatMobileTooltip.proRataVolume.description",
              )}
            </li>
          </ul>
          <p className="pt-2">
            <b>
              {t(
                "cockpit.managementTile.roaming.showOption.flatMobileTooltip.example.title",
              )}
            </b>
            <br />
            {t(
              "cockpit.managementTile.roaming.showOption.flatMobileTooltip.example.description",
            )}
          </p>
        </>
      }
    />
  );
};
